import { useId, useRef, useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  invalidation,
  isApiError,
  isDemoMode,
  message,
  type AccountInfo,
  type ApiClient,
} from '@sl/core';
// The module, not the `tree/` barrel: the shell must not pull the tree table into its own chunk.
import { clearChartColorsFor } from '../../charts/chartColors/clearChartColors';
import { clearExpansionFor } from '../../tree/clearExpansion';
// Same reason: `search/recent.ts` on its own, not the `search/` barrel, which reaches the palette.
import { clearRecent } from '../../search/recent';
import { Button } from '../../ui/Button/Button';
import { Modal } from '../../ui/Modal/Modal';
import { Popover } from '../../ui/Popover/Popover';
import { useToast } from '../../ui/Toast/ToastProvider';
import { useAppConfig } from '../configContext';
import { messageText } from '../messages';
import { RETURNING_SCENARIO, goToScenario } from '../scenario';
import { HOME_PATH, goToServer } from '../server';
import { testId } from '../testId';
import { MODE_EXPLAINER, SHELL_COPY } from './copy';
import { SurfaceModeChip } from './ModeChip';
import { useShell } from './shellContext';
import { StartFreshDialog } from './StartFresh';
import styles from './AppShell.module.css';

// The mode menu — REQ-006 AC2: what the current mode means for looking, planning and publishing, and, on a
// demo account, the demo controls. It is a Popover rather than a `role="menu"` because its first job is to
// EXPLAIN: a menu may hold commands, not paragraphs. The chip is its trigger (`aria-haspopup="dialog"`),
// Escape and an outside click close it and focus returns to the chip (TEST_PLAN §1.6 K6).
//
// "Reset demo" (REQ-001 AC4) asks first: an `alertdialog` with what will happen, then `resetDemo` + the
// invalidation row of `@sl/core/queries` — nothing here knows which query keys that touches.
// "Start fresh" (REQ-001 AC6) is the second way back and it is REAL from T-099: the same two-step shape, the
// `start_fresh` seed behind it, and Home as the landing. D-114 parked it because an account with no plan and
// no rules had nowhere to land; Home answers that state in its own words now (no-plan pacing, an empty
// inbox), so the control is drawn again. D-128's rule — neither build draws it until it works — is kept: the
// flow, its ids and its words all arrive together, and T-105 takes the same words to the phone.
//
// **T-432 — "Switch to my ad account", the other half of a two-way door** (REQ-002 AC1 at REQUIREMENTS v2.9).
// The welcome screen's "Open my ad account" gets a reader INTO their account; this is how they get back after
// taking the demo, instead of typing `?server=api` or clearing `sl.server`. It is drawn on one state only — an
// API build showing the in-memory demo — and it is a fact about the SERVER, not about the mode or the account,
// so it is gated on the build and the transport and sits ABOVE the demo controls: it is the only row here that
// leaves the demo rather than rearranging it.

export interface ModeMenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  menuId: string;
  account: AccountInfo;
  client: ApiClient;
}

export function ModeMenu({ open, onClose, anchorRef, menuId, account, client }: ModeMenuProps) {
  const queryClient = useQueryClient();
  const { messages, session } = useShell();
  const { toast } = useToast();
  const explainerId = useId();
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [confirmingStartFresh, setConfirmingStartFresh] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const resetTriggerRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  const explainer = MODE_EXPLAINER[account.mode];
  const isDemo = isDemoMode(account.mode);
  const previewOn = account.mode === 'demo_view_only_preview';
  const config = useAppConfig();
  // T-330: which scenario is on screen is a fact about the ADDRESS the app was opened at (`scenario.ts`).
  const returningOn = config.qa.demoScenario === RETURNING_SCENARIO;
  // T-432 — the way back exists only where there is something to go back TO: a build that HAS an account
  // (`apiBuild`, which no address or stored value can turn on) and is currently showing the demo server.
  const canSwitchToLive = config.apiBuild && config.transport === 'demo';

  const fail = (error: unknown) => {
    const sentence = isApiError(error) ? error.userMessage : message('error.unknown');
    toast({ message: messageText(sentence, messages), tone: 'error' });
  };

  const onReset = async () => {
    setResetting(true);
    try {
      await client.resetDemo(account.id);
      // Which tree rows were open is remembered outside the demo namespace (D-097 (7)), so the shell
      // forgets it by hand — "Reset demo" returns the tree to collapsed (REQ-910 AC3).
      await clearExpansionFor({ userId: session.user.id, accountId: account.id });
      // …and the spend chart's colour settings, which live at `sl.chartColors.<account>` outside the demo
      // namespace for the same reason (REQ-025 AC8: "Reset demo" and "Start fresh" clear it too).
      await clearChartColorsFor({ accountId: account.id });
      // The last five things you opened from search are `localStorage`, deliberately outside the demo
      // namespace too (T-044's note (6)) — so "Reset demo" forgets them by hand as well, or a reset demo
      // still remembers the campaign you looked at before it.
      clearRecent(account.id);
      await queryClient.invalidateQueries(invalidation.resetDemo(account.id));
      setConfirmingReset(false);
      // The toast announces it through the ONE live region; nothing here announces twice (T-035).
      toast({ message: SHELL_COPY.resetDemoDone, tone: 'success', key: 'shell.reset-demo' });
    } catch (error) {
      fail(error);
    } finally {
      setResetting(false);
    }
  };

  const onTogglePreview = async () => {
    setPreviewBusy(true);
    try {
      await client.setViewOnlyPreview(account.id, !previewOn);
      await queryClient.invalidateQueries(invalidation.setViewOnlyPreview(account.id));
      onClose();
      toast({
        message: previewOn ? SHELL_COPY.previewOff : SHELL_COPY.previewOn,
        tone: 'info',
      });
    } catch (error) {
      fail(error);
    } finally {
      setPreviewBusy(false);
    }
  };

  return (
    <>
      <Popover
        open={open}
        onClose={onClose}
        anchorRef={anchorRef}
        id={menuId}
        placement="bottom-start"
        width={340}
        aria-label={SHELL_COPY.modeMenuTitle}
        testID={testId('shell', 'mode-menu')}
      >
        {/* `explainer.mode` (TEST_PLAN §1.4): the glossary scan skips this subtree, as it does every explainer. */}
        <div className={styles.modeExplainer} data-testid={testId('explainer', 'mode')}>
          <h2 className={styles.modeTitle} id={explainerId}>
            {explainer.title}
          </h2>
          {explainer.lines.map((line) => (
            <p key={line} className={styles.modeLine}>
              {line}
            </p>
          ))}
        </div>
        {isDemo || canSwitchToLive ? (
          <div className={styles.modeActions}>
            {/* T-432 — `lock` is the live, view-only account's own glyph (`MODE_ICON.live_view_only`), so the
                row wears the state it leads to. A navigation, so the menu closes first; it lands on Home
                because the demo's entity ids are not the live account's (`app/server.ts`'s HOME_PATH). */}
            {canSwitchToLive ? (
              <Button
                block
                icon="lock"
                testID={testId('mode', 'switch-to-live')}
                onClick={() => {
                  onClose();
                  goToServer('api', HOME_PATH);
                }}
              >
                {SHELL_COPY.switchToMyAdAccount}
              </Button>
            ) : null}
            {isDemo ? (
              <>
                {/* REQ-100 AC5's two entries are ONE control with two states, and the id says which one is
                on screen: a crawl that finds `mode.back-to-full-demo` knows the preview is on without
                reading the label, and `mode.preview-view-only` never names a button that leaves it. */}
                <Button
                  block
                  icon="eye"
                  loading={previewBusy}
                  testID={
                    previewOn
                      ? testId('mode', 'back-to-full-demo')
                      : testId('mode', 'preview-view-only')
                  }
                  onClick={() => void onTogglePreview()}
                >
                  {previewOn ? SHELL_COPY.backToFullDemo : SHELL_COPY.previewViewOnly}
                </Button>
                {/* T-330 (BUG-194, REQ-023 AC1 v2.6): "Since your last visit" reachable by clicking. The same
                one-control-two-states shape as the preview, and the id follows the STATE for the same
                reason: `mode.end-return-visit` on screen means the scenario is on. It is a navigation —
                the scenario is an input of the one `ApiClient` (`scenario.ts`) — so the menu closes first. */}
                <Button
                  block
                  icon="calendar"
                  testID={
                    returningOn
                      ? testId('mode', 'end-return-visit')
                      : testId('mode', 'preview-return-visit')
                  }
                  onClick={() => {
                    onClose();
                    goToScenario(returningOn ? null : RETURNING_SCENARIO);
                  }}
                >
                  {returningOn ? SHELL_COPY.endReturnVisit : SHELL_COPY.previewReturnVisit}
                </Button>
                {account.capabilities.canResetDemo ? (
                  <>
                    <Button
                      ref={resetTriggerRef}
                      block
                      variant="danger-ghost"
                      icon="undo"
                      testID={testId('mode', 'reset-demo')}
                      onClick={() => {
                        onClose();
                        setConfirmingReset(true);
                      }}
                    >
                      {SHELL_COPY.resetDemo}
                    </Button>
                    {/* REQ-001 AC6 — the other way back: the same account with nothing set on it. */}
                    <Button
                      block
                      variant="danger-ghost"
                      // BUG-077 (D-1221): a plus means ADD; this empties the account. The circling arrows.
                      icon="refresh"
                      testID={testId('mode', 'start-fresh')}
                      onClick={() => {
                        onClose();
                        setConfirmingStartFresh(true);
                      }}
                    >
                      {SHELL_COPY.startFresh}
                    </Button>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
      </Popover>

      <Modal
        open={confirmingReset}
        onClose={() => setConfirmingReset(false)}
        role="alertdialog"
        size="sm"
        dismissible={!resetting}
        title={SHELL_COPY.resetDemoTitle}
        subtitle={SHELL_COPY.resetDemoBody}
        initialFocusRef={confirmRef}
        returnFocusRef={anchorRef}
        testID={testId('shell', 'reset-demo')}
        /* REQ-006 AC5 / BUG-009: a centred dialog covers the top bar at 360 px, and this is the one place
           where a demo is about to throw away what you did to it — the mode belongs in front of you. */
        modeChip={<SurfaceModeChip testID={testId('shell', 'reset-demo', 'mode-chip')} />}
        footer={
          <>
            <Button
              variant="ghost"
              disabled={resetting}
              testID={testId('shell', 'reset-demo', 'cancel')}
              onClick={() => setConfirmingReset(false)}
            >
              {SHELL_COPY.resetDemoCancel}
            </Button>
            <Button
              ref={confirmRef}
              variant="danger"
              loading={resetting}
              testID={testId('shell', 'reset-demo', 'confirm')}
              onClick={() => void onReset()}
            >
              {resetting ? SHELL_COPY.resetDemoWorking : SHELL_COPY.resetDemoConfirm}
            </Button>
          </>
        }
      />

      {/* REQ-001 AC6 — the same dialog Settings raises; the flow lives in one file (`StartFresh.tsx`). */}
      <StartFreshDialog
        open={confirmingStartFresh}
        onClose={() => setConfirmingStartFresh(false)}
        returnFocusRef={anchorRef}
      />
    </>
  );
}
